import React, { useState, useEffect, useRef } from 'react';
import BulkUpload from "./BulkUpload";
import UserRoleFunctionSelector from "./UserRoleFunctionSelector";
import { inviteBulk } from "../../services/inviteTeamServices";
import { useUserInfo } from "../../hook/useUserInfo";

interface InviteMember {
  name: string;
  email: string;
  functionIds: string[];
  groupId?: string;
}

interface Function {
  id: string;
  name: string;
  description?: string;
}

interface Group {
  id: string;
  name: string;
}

interface InviteModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInvitesSent: (members: InviteMember[]) => void;
  tenantId: string;
  groupId: string;
  functions: Function[];
  groups: Group[];
}

const InviteModal = ({ open, onOpenChange, onInvitesSent, tenantId, groupId, functions, groups }: InviteModalProps) => {
  const [activeTab, setActiveTab] = useState("individual");
  const [members, setMembers] = useState<InviteMember[]>([
    { name: '', email: '', functionIds: [], groupId: groupId || '' }
  ]);
  const [duplicateWarning, setDuplicateWarning] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingFunctions, setIsLoadingFunctions] = useState(false);
  const [successCount, setSuccessCount] = useState(0);
  const modalRef = useRef<HTMLDivElement>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);

  // Mock toast function
  const showToast = (msg: string) => window.alert(msg);

  // Use loading state if data is not yet available
  useEffect(() => {
    setIsLoadingFunctions(open && (functions.length === 0 || groups.length === 0));
  }, [open, functions, groups]);

  // Focus management
  useEffect(() => {
    if (open && firstInputRef.current) {
      setTimeout(() => {
        firstInputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      // Reset form when modal closes
      setMembers([{ name: '', email: '', functionIds: [], groupId: groupId || '' }]);
      setDuplicateWarning([]);
      setSuccessCount(0);
    }
  }, [open]);

  const handleAddMember = () => {
    setMembers(prev => [...prev, { name: '', email: '', functionIds: [], groupId: groupId || '' }]);
  };

  const handleRemoveMember = (index: number) => {
    setMembers(prev => prev.filter((_, i) => i !== index));
  };

  const handleMemberChange = (index: number, field: keyof InviteMember, value: any) => {
    setMembers(prev => prev.map((member, i) => 
      i === index ? { ...member, [field]: value } : member
    ));

    // Check for duplicate emails
    if (field === 'email' && value) {
      const emails = members.map((m, i) => i === index ? value : m.email).filter(Boolean);
      const duplicates = emails.filter((email, i) => emails.indexOf(email) !== i);
      setDuplicateWarning(duplicates);
    }
  };

  const handleSubmitIndividual = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!tenantId) {
      showToast("Error: Unable to determine your organization. Please try again.");
      return;
    }

    const validMembers = members.filter(m => m.name && m.email && isValidEmail(m.email) && m.functionIds.length > 0);
    
    if (validMembers.length === 0) {
      showToast("Validation Error: Please fill all required fields for at least one member.");
      return;
    }

    if (duplicateWarning.length > 0) {
      showToast("Duplicate Emails: Please fix duplicate email addresses before submitting.");
      return;
    }

    setIsSubmitting(true);
    let successCounter = 0;
    const successfullyInvitedMembers: InviteMember[] = [];

    try {
      // Transform members to match bulk API format
      const invitations = validMembers.map(member => ({
        email: member.email,
        fullName: member.name,
        groupId: member.groupId || groupId,
        functionIds: member.functionIds,
        tenantId: tenantId
      }));

      const response = await inviteBulk(invitations);
      
      if (response.success) {
        successCounter = validMembers.length;
        successfullyInvitedMembers.push(...validMembers);
        
        showToast(`Invites sent successfully: ${successCounter} out of ${validMembers.length} invites were sent.`);
        
        onInvitesSent(successfullyInvitedMembers.map(m => ({
          ...m,
          id: `${m.email}-${Date.now()}`,
          status: 'pending',
          expiresAt: new Date(Date.now() + 5 * 60 * 1000),
          createdAt: new Date()
        })));
        
        onOpenChange(false);
      } else {
        throw new Error(response.message || "Failed to send invitations");
      }
    } catch (error) {
      console.error("Failed to send invitations:", error);
      showToast("Failed to send invitations: " + (error instanceof Error ? error.message : 'Unknown error occurred'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBulkUpload = async (csvData: any[]) => {
    if (!tenantId) {
      showToast("Error: Unable to determine your organization. Please try again.");
      return;
    }

    setIsSubmitting(true);
    try {
      // Map groupName to groupId and function names/IDs to functionIds
      const invitations = csvData.map((row, idx) => {
        const group = groups.find(g => g.name.trim().toLowerCase() === (row.groupName || '').trim().toLowerCase());
        if (!group) throw new Error(`Row ${idx + 2}: Group '${row.groupName}' not found. Please use one of: ${groups.map(g => g.name).join(', ')}`);

        let functionIds: string[] = [];
        if (row.functionIds && Array.isArray(row.functionIds) && typeof row.functionIds[0] === 'string') {
          // Accept both function names and IDs
          functionIds = row.functionIds.map((fn: string) => {
            let func = functions.find((f: any) => f.name.trim().toLowerCase() === fn.trim().toLowerCase());
            if (!func) {
              func = functions.find((f: any) => f.id === fn.trim());
            }
            if (!func) throw new Error(`Row ${idx + 2}: Function '${fn}' not found. Please use one of: ${functions.map(f => f.name).join(', ')}`);
            return func.id;
          });
        } else if (row.functionNames && typeof row.functionNames === 'string') {
          const functionNamesArr = row.functionNames.split(',').map((fn: string) => fn.trim()).filter(Boolean);
          functionIds = functionNamesArr.map((fn: string) => {
            let func = functions.find((f: any) => f.name.trim().toLowerCase() === fn.toLowerCase());
            if (!func) {
              func = functions.find((f: any) => f.id === fn.trim());
            }
            if (!func) throw new Error(`Row ${idx + 2}: Function '${fn}' not found. Please use one of: ${functions.map(f => f.name).join(', ')}`);
            return func.id;
          });
        }

        if (!functionIds.length) {
          throw new Error(`Row ${idx + 2}: No valid functions assigned. Please use one of: ${functions.map(f => f.name).join(', ')}`);
        }

        return {
          email: row.email,
          fullName: row.fullName || row.name,
          groupId: group.id,
          functionIds,
          tenantId: tenantId
        };
      });

      const response = await inviteBulk(invitations);
      
      if (response.success) {
        showToast(`Bulk upload successful: ${invitations.length} members invited successfully.`);
        onInvitesSent(csvData.map((m: any) => ({
          ...m,
          expiresAt: new Date(Date.now() + 5 * 60 * 1000),
          createdAt: new Date()
        })));
        onOpenChange(false);
      } else {
        throw new Error(response.message || "Bulk upload failed");
      }
    } catch (error) {
      console.error("Bulk upload failed:", error);
      showToast("Bulk upload failed: " + (error instanceof Error ? error.message : 'Unknown error occurred'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const downloadCSVTemplate = () => {
    // Build dynamic lists
    const groupNames = groups.map(g => g.name).join(', ');
    const functionNames = functions.map((f: any) => f.name).join(', ');
    const instructions = [
      '# Please fill in the following columns.',
      '# Use the exact group and function names as shown below.',
      '# Multiple function names should be separated by commas.',
      `# Available group names: ${groupNames}`,
      `# Available function names: ${functionNames}`,
    ];
    const headers = ['email', 'fullName', 'groupName', 'functionNames'];
    const sampleData = [
      ['john.doe@example.com', 'John Doe', groups[0]?.name || '', functions.slice(0,2).map((f: any) => f.name).join(', ')],
      ['jane.smith@example.com', 'Jane Smith', groups[1]?.name || '', functions[0]?.name || ''],
    ];
    
    const csvContent = [
      ...instructions,
      headers.join(','),
      ...sampleData.map(row => row.join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'team_invitations_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    showToast("Template downloaded: CSV template has been downloaded. Please fill it with your team data.");
  };

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  if (!tenantId) {
    return (
      <div className="alert alert-danger d-flex align-items-start gap-2" role="alert">
        <span className="fw-bold me-2">!</span>
        <div>
          <div className="fw-semibold">Unable to determine your organization. Please refresh the page or contact support.</div>
        </div>
      </div>
    );
  }

if (!open) return null;

return (
  <div className="modal fade show" tabIndex={-1} style={{ display: 'block', background: 'rgba(0,0,0,0.18)' }}>
    <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
      <div className="modal-content" ref={modalRef} aria-modal="true" aria-labelledby="invite-modal-title" style={{ borderRadius: 18 }}>
        <div  style={{ padding: '1px 28px' }}>
          <h2 id="invite-modal-title" className="modal-title" >Invite Team Members</h2>
          <button type="button" className="btn-close btn-close-white" aria-label="Close" onClick={() => onOpenChange(false)}></button>
        </div>
        <div className="modal-body" style={{ padding: '20px' }}>
         <ul className="nav nav-tabs mb-4 d-flex w-100" role="tablist">
  <li className="nav-item flex-fill text-center" role="presentation">
    <button
      className={`nav-link w-100${activeTab === 'individual' ? ' active' : ''}`}
      type="button"
      onClick={() => setActiveTab('individual')}
      role="tab"
    >
      Individual
    </button>
  </li>
  <li className="nav-item flex-fill text-center" role="presentation">
    <button
      className={`nav-link w-100${activeTab === 'bulk' ? ' active' : ''}`}
      type="button"
      onClick={() => setActiveTab('bulk')}
      role="tab"
    >
      Bulk Upload
    </button>
  </li>
</ul>


          {isLoadingFunctions && (
            <div className="d-flex align-items-center justify-content-center py-4">
              <span className="spinner-border 
               me-2" role="status" aria-hidden="true"></span>
              <span>Loading business functions...</span>
            </div>
          )}

          {successCount > 0 && (
            <div className="alert alert-success d-flex align-items-start gap-2 mb-4" role="alert">
              <span className="fw-bold me-2">✔</span>
              <div>Successfully sent {successCount} invitation{successCount !== 1 ? 's' : ''}.</div>
            </div>
          )}

          {duplicateWarning.length > 0 && (
            <div className="alert alert-danger d-flex align-items-start gap-2 mb-4" role="alert">
              <span className="fw-bold me-2">!</span>
              <div>Duplicate email addresses detected: {duplicateWarning.join(', ')}</div>
            </div>
          )}

          {!isLoadingFunctions && activeTab === 'individual' && (
            <form onSubmit={handleSubmitIndividual}>
              {members.map((member, index) => (
                <div key={index} className="mb-4 border rounded bg-white shadow-sm p-4">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h4 className="fw-medium mb-0 
                    " style={{ fontWeight: 600, fontSize: 20 }}>Member {index + 1}</h4>
                    {members.length > 1 && (
                      <button
                        type="button"
                        className="btn btn-link text-danger p-0 fs-4 lh-1"
                        onClick={() => handleRemoveMember(index)}
                        aria-label={`Remove member ${index + 1}`}
                        style={{ fontSize: 28, fontWeight: 700, color: '#e53935', textDecoration: 'none' }}
                      >
                        ×
                      </button>
                    )}
                  </div>
                  <div className="row g-4 mb-3">
                    <div className="col-md-6">
                      <label htmlFor={`name-${index}`} className="form-label ">Full Name *</label>
                      <input
                        ref={index === 0 ? firstInputRef : undefined}
                        id={`name-${index}`}
                        type="text"
                        placeholder="John Doe"
                        value={member.name}
                        onChange={(e) => handleMemberChange(index, 'name', e.target.value)}
                        required
                        className="form-control"
                        aria-describedby={`name-${index}-help`}
                        style={{ minHeight: 48 }}
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor={`email-${index}`} className="form-label ">Email Address *</label>
                      <input
                        id={`email-${index}`}
                        type="email"
                        placeholder="john@company.com"
                        value={member.email}
                        onChange={(e) => handleMemberChange(index, 'email', e.target.value)}
                        required
                        className={`form-control${member.email && !isValidEmail(member.email) ? ' is-invalid' : ''}`}
                        aria-describedby={`email-${index}-help`}
                        style={{ minHeight: 48 }}
                      />
                      {member.email && !isValidEmail(member.email) && (
                        <div className="invalid-feedback d-block">Please enter a valid email address</div>
                      )}
                    </div>
                  </div>
                  {/* User Role Section */}
                  <div className="mb-3">
                   
                    <div className=" ">
  {functions.length > 0 ? (
                          <UserRoleFunctionSelector
                            functions={functions}
                            groups={groups}
                            selectedGroupId={member.groupId || ''}
                            selectedFunctionIds={member.functionIds}
                            onGroupChange={(groupId) => handleMemberChange(index, 'groupId', groupId)}
                            onFunctionIdsChange={(functionIds) => handleMemberChange(index, 'functionIds', functionIds)}
                          />
                        ) : (
                          <div className="text-center py-3 text-secondary">
                            No business functions available
                          </div>
                        )}
                    </div>
                  </div>
                  {/* Function Assignments */}
                
                </div>
              ))}
              <div className="d-flex align-items-center justify-content-between mt-4">
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={handleAddMember}
                  disabled={isSubmitting}
                >
                  <i className="bi bi-plus-lg me-2"></i>
                  Add Another Member
                </button>
                <button
                  type="submit"
                  className="btn btn-primary px-4"
                  disabled={isSubmitting || !members.some(m => m.name && isValidEmail(m.email) && m.functionIds.length > 0) || duplicateWarning.length > 0}
                >
                  {isSubmitting ? 'Sending...' : 'Send Invitations'}
                </button>
              </div>
            </form>
          )}

          {!isLoadingFunctions && activeTab === 'bulk' && (
            <BulkUpload
              onUpload={handleBulkUpload}
              isSubmitting={isSubmitting}
              functions={functions}
              groups={groups}
              tenantId={tenantId}
            />
          )}
        </div>
      </div>
    </div>
  </div>
);
};

export default InviteModal;